import React, { useCallback, useMemo } from "react";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { CounterpartyDirection } from "@common/helpers/counterparties";
import { LedgerSelect } from "@components/ledger/LedgerSelect";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import {
    DateOnlyDateRange,
    toDateOnlyDateRange,
    toLocalDateRange,
} from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { useClasses } from "@api/class.api";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { ArAgingBucket } from "@common/types/reports/ar-aging";
import {
    ACCOUNTING_REPORT_COMPARISON_OPTIONS,
    AccountingReportType,
    NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS,
} from "@common/types/reports/report-type";
import { AccountingReportCycle } from "@common/constants";
import { ConsolidationGroupResponse } from "@common/schemas/domains/accounting/consolidation.schema";
import { useSelectedLedger } from "@hooks/useSelectedLedger";
import { useEntities } from "@hooks/useEntities";
import { ALL_TIME_PRESET_ID } from "@helpers/date";
import { HStack } from "@design-system/HStack/HStack";
import { TablePageHeader } from "../pageHeader/TablePageHeader";
import { DirectionRadioDef } from "../pageHeader/filters/Tabs/AmountFilter/directionRadios";
import { CoaTemplateGroupOption } from "../pageHeader/filters/Tabs/CoaTemplateGroupsFilter/CoaTemplateGroupsFilter.lib";
import { useOverlays } from "../../contexts/OverlayContext";
import { AccountingEntitySelect } from "./AccountingEntitySelect";
import { ConsolidatedEntitySelect } from "./ConsolidatedEntitySelect";
import { AccountingReportFilters } from "./types";
import {
    type AccountingReportFilterProps,
    getAccountingReportAvailableFilters,
    getAccountingReportFilterOverlayProps,
    partitionAgingBucketsFromFilters,
} from "./accountingReportFilters.lib";
import styles from "./AccountingReportFiltersForm.module.scss";
import type {
    DatePickerPreset,
    DateOnlyRange,
} from "@design-system/DatePicker/DatePicker.types";
import { SchedulesReportFilterOption } from "@components/pageHeader/filters/Tabs/SchedulesReportFilters/OptionsMultiselectFilter";
import { FinancialAccount } from "@common/types/financialAccount";
import { useEntitiesWithAccountingAvailable } from "./useEntitiesWithAccountingAvailable";
import { AccountingFiltersSetter } from "./useAccountingTab";
import { AccountingReportComparisonSelect } from "./AccountingReportComparisonSelect";
import { AccountingReportCycleSelect } from "./AccountingReportCycleSelect";

export interface AccountingReportFiltersFormProps extends AccountingReportFilterProps {
    filters: AccountingReportFilters;
    setFilters: AccountingFiltersSetter;
    additionalButtons?: React.ReactNode;
    beforeFiltersChildren?: React.ReactNode;
    afterFiltersChildren?: React.ReactNode;
    requireDateSelection?: boolean;
    hideCreateJournalEntryButton?: boolean;
    hideDateRangeFilter?: boolean;
    availableClassifications?: AccountClass[];
    hideSearch?: boolean;
    searchPlaceholder?: string;
    staticTemplateGroupOptions?: CoaTemplateGroupOption[];
    directionRadios?: DirectionRadioDef[];
    counterpartyEntityLabel?: string;
    counterpartyDirection?: CounterpartyDirection;
    counterpartySingleSelect?: boolean;
    bindCounterpartyFilterToCustomerId?: boolean;
    /**
     * Restricts the counterparty filter's listed options to counterparties
     * used as the customer on at least one invoice, instead of every
     * counterparty used anywhere in the workspace. Used by the AR Aging
     * Summary/Detail reports.
     */
    counterpartyInvoicesOnly?: boolean;
    hideEntityFilter?: boolean;
    entityFilterOverride?: React.ReactNode;
    // Replaces the default date-range picker (`DateRangeFilterNext`) with a
    // custom control, e.g. a single as-of-date picker for AR Aging (which has
    // no start/end range). Only takes effect when `hideDateRangeFilter` is
    // also set, so the default range behavior for every other report is
    // completely unaffected. Additive, zero-risk override — mirrors the
    // existing `ledgerSelectOverride` / `entityFilterOverride` pattern.
    dateFilterOverride?: React.ReactNode;
    // Replaces the default single-entity ledger selector (e.g. the multi-entity
    // General Ledger basis selector, which resolves the ledger across the
    // selected entities rather than a single one).
    ledgerSelectOverride?: React.ReactNode;
    // When provided (and the consolidation flag is on), the entity selector
    // gains an "All entities (Consolidated)" option that switches the report to
    // the consolidation group layout.
    consolidationGroup?: ConsolidationGroupResponse;
    showCyclesSelect?: boolean;
    reportType?: AccountingReportType;
    hasEntityGrouping?: boolean;
    hasClassGrouping?: boolean;
    hideLedgerSelect?: boolean;
    pinnedPresets?: readonly string[];
    onTogglePin?: (id: string) => void;
    extraDatePresets?: ReadonlyArray<DatePickerPreset<DateOnlyRange>>;
    shouldTrackDatePreset?: boolean;
    // Rendered as the last segment of the date / cycle control group.
    cycleGroupAppend?: (props: { className: string }) => React.ReactNode;
    // Rendered between the ledger selector and the entity selector.
    beforeEntityChildren?: React.ReactNode;
    filterButtonLabel?: string;
    staticHoldingAccountOptions?: SchedulesReportFilterOption[];
    accountFilterPredicate?: (account: FinancialAccount) => boolean;
    shouldShowKickAccountOption?: boolean;
    /**
     * A/R Aging Detail only: surfaces the aging-period filter tab. Unlike
     * every other A/R Aging filter (customer, terms, account, dates), the
     * selected buckets are NOT part of the shared `filters`/`setFilters` tab
     * context — Summary has no bucket concept, and Detail itself keeps
     * `buckets` state outside the tab context (page-level query param on the
     * standalone page, drawer-local state from a Summary drill-down). So
     * `agingBuckets`/`onAgingBucketsChange` are threaded through as a pair of
     * override props, folded into the overlay's filter object for display
     * and intercepted back out on change — same shape as the
     * `bindCounterpartyFilterToCustomerId` override below.
     */
    agingBuckets?: ArAgingBucket[];
    onAgingBucketsChange?: (buckets: ArAgingBucket[] | undefined) => void;
}

export const AccountingReportFiltersForm: React.FC<
    AccountingReportFiltersFormProps
> = ({
    filters,
    setFilters,
    additionalButtons,
    beforeFiltersChildren,
    afterFiltersChildren,
    requireDateSelection,
    showAccountFilter,
    hideCreateJournalEntryButton,
    hideDateRangeFilter = false,
    hideClassFilter = false,
    showAccountEnabledFilter = false,
    showAccountSourceFilter = false,
    showAccountRolePresenceFilter = false,
    showAccountRulePresenceFilter = false,
    showForm1099BoxFilter = false,
    showGroupFilter = false,
    staticTemplateGroupOptions,
    showCyclesSelect = false,
    reportType,
    showJournalEntrySourceFilter = false,
    showAmountFilter = false,
    directionRadios,
    showCounterpartyFilter = false,
    counterpartyEntityLabel,
    counterpartyDirection,
    counterpartySingleSelect = false,
    bindCounterpartyFilterToCustomerId = false,
    counterpartyInvoicesOnly = false,
    showExclude1099Filter = false,
    availableClassifications,
    hideSearch = false,
    searchPlaceholder,
    hideEntityFilter = false,
    entityFilterOverride,
    dateFilterOverride,
    ledgerSelectOverride,
    consolidationGroup,
    hasEntityGrouping = false,
    hasClassGrouping = false,
    hideLedgerSelect = false,
    pinnedPresets,
    onTogglePin,
    extraDatePresets,
    shouldTrackDatePreset = false,
    cycleGroupAppend,
    beforeEntityChildren,
    filterButtonLabel,
    showSchedulesReportFilters = false,
    showArAgingInvoiceFilters = false,
    showInvoiceAccountFilter = false,
    showFinancialAccountFilter = false,
    accountFilterPredicate,
    shouldShowKickAccountOption,
    staticHoldingAccountOptions,
    showAgingPeriodFilter = false,
    agingBuckets,
    onAgingBucketsChange,
}) => {
    const availableEntities = useEntitiesWithAccountingAvailable();
    const allEntities = useEntities();
    const { classesWithChildren } = useClasses();
    const comparisonOptions =
        reportType && !filters.consolidationGroupId
            ? ACCOUNTING_REPORT_COMPARISON_OPTIONS[reportType]
            : NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS;
    const { hasAccess: hasClassesAccess } = useAllowFeature(
        PREMIUM_FEATURES.CLASSES,
    );
    const setFilterValue = useCallback(
        (key: keyof AccountingReportFilters, value: any) => {
            setFilters((currentFilters) => ({
                ...currentFilters,
                [key]: value,
            }));
        },
        [setFilters],
    );

    const selectRange = useCallback(
        (range: DateOnlyDateRange, isAllTime?: boolean, presetId?: string) => {
            const localRange = toLocalDateRange(range);
            setFilters((currentFilters) => ({
                ...currentFilters,
                startDate: localRange.start,
                endDate: localRange.end,
                isAllTime,
                ...(shouldTrackDatePreset && { datePreset: presetId }),
            }));
        },
        [setFilters, shouldTrackDatePreset],
    );

    const selectedDatePresetId = filters.isAllTime
        ? ALL_TIME_PRESET_ID
        : (filters.datePreset ?? null);

    const dateRange = useMemo(
        () =>
            toDateOnlyDateRange({
                start: filters.startDate,
                end: filters.endDate,
            }),
        [filters.startDate, filters.endDate],
    );

    const selectedEntity = useMemo(
        () => availableEntities.find((e) => e.id === filters.entityId),
        [filters.entityId, availableEntities],
    );

    // V1 supports a single consolidation group per workspace, so the
    // consolidated option is labelled with that group's parent entity name.
    // The parent is resolved against all workspace entities (not the
    // accounting-filtered subset) since it always exists when a group does.
    const consolidatedLabel = useMemo(() => {
        const parentEntity = allEntities.find(
            (e) => e.uuid === consolidationGroup?.parentEntityUuid,
        );
        return parentEntity?.name
            ? `${parentEntity.name} (Consolidated)`
            : "All entities (Consolidated)";
    }, [allEntities, consolidationGroup?.parentEntityUuid]);
    const { selectedLedger } = useSelectedLedger(
        selectedEntity?.id,
        filters.ledgerId,
    );

    const availableFilters = getAccountingReportAvailableFilters({
        hideClassFilter,
        showAccountFilter,
        showAccountEnabledFilter,
        showAccountSourceFilter,
        showAccountRolePresenceFilter,
        showAccountRulePresenceFilter,
        showForm1099BoxFilter,
        showGroupFilter,
        showJournalEntrySourceFilter,
        showAmountFilter,
        showCounterpartyFilter,
        showExclude1099Filter,
        showSchedulesReportFilters,
        showArAgingInvoiceFilters,
        showInvoiceAccountFilter,
        showFinancialAccountFilter,
        showAgingPeriodFilter,
        hasClassesAccess,
    });

    const entitySelectControl = consolidationGroup ? (
        <ConsolidatedEntitySelect
            entities={availableEntities}
            selectedEntity={selectedEntity}
            isConsolidated={Boolean(filters.consolidationGroupId)}
            consolidatedLabel={consolidatedLabel}
            onSelectEntity={(e) =>
                setFilters((currentFilters) => ({
                    ...currentFilters,
                    entityId: e?.id,
                    consolidationGroupId: undefined,
                }))
            }
            onSelectConsolidated={() =>
                setFilters((currentFilters) => ({
                    ...currentFilters,
                    consolidationGroupId: consolidationGroup.id,
                    // Consolidated reports render a fixed entity/eliminations/
                    // total layout, so cycle/comparison/ratio controls are
                    // hidden. Clear their values too, otherwise stale filters
                    // would still be sent to the backend.
                    cycle: undefined,
                    comparisons: undefined,
                    comparisonDiffs: undefined,
                    ratioTypes: undefined,
                }))
            }
            className={styles.entitySelect}
            disabled={filters.cycle === AccountingReportCycle.ENTITY}
        />
    ) : (
        <AccountingEntitySelect
            entities={availableEntities}
            selectedEntity={selectedEntity}
            onChange={(e) => setFilterValue("entityId", e?.id)}
            className={styles.entitySelect}
            disabled={filters.cycle === AccountingReportCycle.ENTITY}
        />
    );

    const overlayFilters = useMemo(() => {
        const withCounterparty = bindCounterpartyFilterToCustomerId
            ? {
                  ...filters,
                  counterpartiesIds: filters.customerId
                      ? [filters.customerId]
                      : undefined,
              }
            : filters;
        return showAgingPeriodFilter
            ? { ...withCounterparty, agingBuckets }
            : withCounterparty;
    }, [
        bindCounterpartyFilterToCustomerId,
        filters,
        showAgingPeriodFilter,
        agingBuckets,
    ]);

    const { openOverlay } = useOverlays();
    const handleShowJournalEntryModal = useCallback(() => {
        if (!selectedEntity) {
            return;
        }

        openOverlay("journalEntryDrawer", {
            entity: selectedEntity,
            ledgerBasis: selectedLedger?.basis,
        });
    }, [selectedEntity, selectedLedger, openOverlay]);

    return (
        <TablePageHeader
            search={filters.search}
            onSearchChange={
                hideSearch ? undefined : (val) => setFilterValue("search", val)
            }
            searchPlaceholder={searchPlaceholder}
            availableFilters={availableFilters}
            beforeFiltersChildren={beforeFiltersChildren}
            afterFiltersChildren={afterFiltersChildren}
            prependChildren={
                hideDateRangeFilter ? (
                    dateFilterOverride
                ) : (
                    <HStack gap={8}>
                        <div
                            className={
                                showCyclesSelect
                                    ? styles.filterGroup
                                    : undefined
                            }
                        >
                            <DateRangeFilterNext
                                onChange={selectRange}
                                dateRange={dateRange}
                                requireSelection={requireDateSelection}
                                pinnedPresets={pinnedPresets}
                                onTogglePin={onTogglePin}
                                extraPresets={extraDatePresets}
                                selectedPresetId={
                                    shouldTrackDatePreset
                                        ? selectedDatePresetId
                                        : undefined
                                }
                                buttonClass={
                                    showCyclesSelect
                                        ? styles.dateRangeFilter
                                        : undefined
                                }
                            />
                            {showCyclesSelect && (
                                <AccountingReportCycleSelect
                                    className={
                                        cycleGroupAppend
                                            ? styles.cycleSelectMiddle
                                            : undefined
                                    }
                                    value={filters.cycle}
                                    onChange={(cycle) =>
                                        setFilterValue("cycle", cycle)
                                    }
                                    showEntityOption={
                                        hasEntityGrouping &&
                                        availableEntities.length >= 2
                                    }
                                    showClassOption={
                                        hasClassGrouping &&
                                        classesWithChildren.length >= 1
                                    }
                                />
                            )}
                            {cycleGroupAppend?.({
                                className: styles.filterGroupAppendSelect,
                            })}
                        </div>
                        {comparisonOptions.comparisons.length > 0 && (
                            <AccountingReportComparisonSelect
                                comparisons={filters.comparisons ?? []}
                                comparisonDiffs={filters.comparisonDiffs ?? []}
                                ratioTypes={filters.ratioTypes ?? []}
                                availableOptions={comparisonOptions}
                                onChange={(newValue) => {
                                    setFilters((currentFilters) => ({
                                        ...currentFilters,
                                        comparisons: newValue.comparisons,
                                        comparisonDiffs:
                                            newValue.comparisonDiffs,
                                        ratioTypes: newValue.ratioTypes,
                                    }));
                                }}
                            />
                        )}
                    </HStack>
                )
            }
            filters={overlayFilters}
            onFiltersChange={(incomingFilters) => {
                let newFilters = incomingFilters;
                if (showAgingPeriodFilter && "agingBuckets" in newFilters) {
                    const partition =
                        partitionAgingBucketsFromFilters(newFilters);
                    onAgingBucketsChange?.(partition.agingBuckets);
                    if (!partition.hasRestChanges) {
                        return;
                    }
                    newFilters = partition.restFilters;
                }
                setFilters((currentFilters) => {
                    const next = {
                        ...currentFilters,
                        ...newFilters,
                    };
                    if (
                        bindCounterpartyFilterToCustomerId &&
                        "counterpartiesIds" in newFilters
                    ) {
                        return {
                            ...next,
                            customerId: next.counterpartiesIds?.[0],
                            counterpartiesIds: undefined,
                        };
                    }
                    return next;
                });
            }}
            {...getAccountingReportFilterOverlayProps({
                accountFilterPredicate,
                shouldShowKickAccountOption,
                counterpartyEntityLabel,
                counterpartyDirection,
                counterpartyInvoicesOnly,
                counterpartySingleSelect,
                bindCounterpartyFilterToCustomerId,
                directionRadios,
            })}
            hardcodedEntityId={selectedEntity?.id}
            availableClassifications={availableClassifications}
            staticTemplateGroupOptions={staticTemplateGroupOptions}
            staticHoldingAccountOptions={staticHoldingAccountOptions}
            filterButtonLabel={filterButtonLabel}
            isFullPage
        >
            {!hideLedgerSelect &&
                (ledgerSelectOverride ?? (
                    <LedgerSelect
                        className={styles.ledgerSelect}
                        selectedEntity={selectedEntity}
                        selectedLedgerId={filters.ledgerId}
                        onChange={(ledgerId) =>
                            setFilterValue("ledgerId", ledgerId)
                        }
                    />
                ))}

            {beforeEntityChildren}

            {entityFilterOverride !== undefined
                ? entityFilterOverride
                : !hideEntityFilter && entitySelectControl}

            {additionalButtons}
            {!hideCreateJournalEntryButton && (
                <WithHotkey
                    hotKey="alt+n"
                    hotKeyOptions={{
                        description: "Create journal entry",
                    }}
                >
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleShowJournalEntryModal}
                    >
                        Create journal entry
                    </Button>
                </WithHotkey>
            )}
        </TablePageHeader>
    );
};
